import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Global translations (common UI, errors, navigation)
// ❌ Do NOT edit vi.json/en.json directly for module-specific translations!
// ✅ Use module-specific locales: src/modules/YourModule/locales/{vi,en}.json
// See: docs/i18n-guidelines.md
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
import en from './langs/en.json'
import vi from './langs/vi.json'

// Module-specific translations
// To add new module: import and register in resources below
import viEmployee from '@/modules/Employee/locales/vi.json'
import enEmployee from '@/modules/Employee/locales/en.json'
import viCustomer from '@/modules/Customer/locales/vi.json'
import enCustomer from '@/modules/Customer/locales/en.json'
import viResume from '@/modules/Resume/locales/vi.json'
import enResume from '@/modules/Resume/locales/en.json'
import viReport from '@/modules/Report/locales/vi.json'
import enReport from '@/modules/Report/locales/en.json'
import viTicket from '@/modules/Ticket/locales/vi.json'
import enTicket from '@/modules/Ticket/locales/en.json'
import viOncall from '@/modules/Oncall/locales/vi.json'
import enOncall from '@/modules/Oncall/locales/en.json'
import viWorkDay from '@/modules/WorkDay/locales/vi.json'
import enWorkDay from '@/modules/WorkDay/locales/en.json'
import viDashboard from '@/modules/Dashboard/locales/vi.json'
import enDashboard from '@/modules/Dashboard/locales/en.json'
import viAffiliate from '@/modules/Affiliate/locales/vi.json'
import enAffiliate from '@/modules/Affiliate/locales/en.json'
import viReseller from '@/modules/Reseller/locales/vi.json'
import enReseller from '@/modules/Reseller/locales/en.json'
import viServices from '@/modules/Service/locales/vi.json'
import enServices from '@/modules/Service/locales/en.json'
import viDayoffs from '@/modules/Dayoffs/locales/vi.json'
import enDayoffs from '@/modules/Dayoffs/locales/en.json'
import viBusiness from '@/modules/Business/locales/vi.json'
import enBusiness from '@/modules/Business/locales/en.json'
import viSurveys from '@/modules/Survey/locales/vi.json'
import enSurveys from '@/modules/Survey/locales/en.json'
import viReleaseNotes from '@/modules/ReleaseNotes/locales/vi.json'
import enReleaseNotes from '@/modules/ReleaseNotes/locales/en.json'
import viTenders from '@/modules/Tenders/locales/vi.json'
// import enTenders from '@/modules/Tenders/locales/en.json'
import viContract from '@/modules/Contract/locales/vi.json'
import enContract from '@/modules/Contract/locales/en.json'
import viMarketing from '@/modules/Marketing/locales/vi.json'
import enMarketing from '@/modules/Marketing/locales/en.json'
import viWatchpoint from '@/modules/Technical/Watchpoint/locales/vi.json'
import enWatchpoint from '@/modules/Technical/Watchpoint/locales/en.json'
import viThreshold from '@/modules/Technical/Threshold/locales/vi.json'
import enThreshold from '@/modules/Technical/Threshold/locales/en.json'
import viSearch from '@/modules/Search/locales/vi.json'
import enSearch from '@/modules/Search/locales/en.json'
import viReportWaf from '@/modules/Technical/Report/locales/vi.json'
import enReportWaf from '@/modules/Technical/Report/locales/en.json'
import viInvoice from '@/modules/Invoice/locales/vi.json'
import enInvoice from '@/modules/Invoice/locales/en.json'
import viSettings from '@/modules/Settings/locales/vi.json'
import enSettings from '@/modules/Settings/locales/en.json'
import viAuthorization from '@/modules/Authorization/locales/vi.json'
import enAuthorization from '@/modules/Authorization/locales/en.json'
import viMailerCampaign from '@/modules/MailerCampaign/locales/vi.json'
import enMailerCampaign from '@/modules/MailerCampaign/locales/en.json'
import viRegulations from '@/modules/Regulations/locales/vi.json'
import enRegulations from '@/modules/Regulations/locales/en.json'

// Component translations
import viDataTable from '@/components/ui/data-table-customized/locales/vi.json'
import enDataTable from '@/components/ui/data-table-customized/locales/en.json'
import viAsset from '@/modules/Asset/locales/vi.json'
import enAsset from '@/modules/Asset/locales/en.json'
import viLivechat from '@/modules/Livechat/locales/vi.json'
import enLivechat from '@/modules/Livechat/locales/en.json'
import viVCard from '@/modules/VCard/locales/vi.json'
import enVCard from '@/modules/VCard/locales/en.json'

// Axios error translations
import viAxiosError from './locales/axios-error/vi.json'
import enAxiosError from './locales/axios-error/en.json'

// Shared keys consumed via the default `translation` namespace (e.g. loading,
// common employee fields). Merged into `translation` so they resolve without a
// namespace, while keeping langs/{vi,en}.json (guarded against direct edits) untouched.
import enCommon from './locales/common/en.json'
import viCommon from './locales/common/vi.json'

const resources = {
    en: {
        translation: { ...en, ...enCommon },
        employee: enEmployee,
        customer: enCustomer,
        resume: enResume,
        report: enReport,
        ticket: enTicket,
        oncall: enOncall,
        workday: enWorkDay,
        dashboard: enDashboard,
        affiliate: enAffiliate,
        reseller: enReseller,
        services: enServices,
        dayoffs: enDayoffs,
        business: enBusiness,
        surveys: enSurveys,
        'release-notes': enReleaseNotes,
        contract: enContract,
        marketing: enMarketing,
        watchpoint: enWatchpoint,
        threshold: enThreshold,
        search: enSearch,
        report_waf: enReportWaf,
        invoice: enInvoice,
        settings: enSettings,
        authorization: enAuthorization,
        mailerCampaign: enMailerCampaign,
        regulations: enRegulations,
        'data-table': enDataTable,
        asset: enAsset,
        livechat: enLivechat,
        vcard: enVCard,
        'axios-error': enAxiosError,
    },
    vi: {
        translation: { ...vi, ...viCommon },
        employee: viEmployee,
        customer: viCustomer,
        resume: viResume,
        report: viReport,
        ticket: viTicket,
        oncall: viOncall,
        workday: viWorkDay,
        dashboard: viDashboard,
        affiliate: viAffiliate,
        reseller: viReseller,
        services: viServices,
        dayoffs: viDayoffs,
        business: viBusiness,
        surveys: viSurveys,
        'release-notes': viReleaseNotes,
        tenders: viTenders,
        contract: viContract,
        marketing: viMarketing,
        watchpoint: viWatchpoint,
        threshold: viThreshold,
        search: viSearch,
        report_waf: viReportWaf,
        invoice: viInvoice,
        settings: viSettings,
        authorization: viAuthorization,
        mailerCampaign: viMailerCampaign,
        regulations: viRegulations,
        'data-table': viDataTable,
        asset: viAsset,
        livechat: viLivechat,
        vcard: viVCard,
        'axios-error': viAxiosError,
    },
}

i18n.use(initReactI18next) // passes i18n down to react-i18next
    .init({
        resources,
        lng: localStorage.getItem('lang') || 'vi',
        // Module namespaces (settings, employee, ...) fall back to the shared
        // `translation` namespace for common keys (cancel, save, loading, ...).
        fallbackNS: 'translation',
        interpolation: {
            escapeValue: false,
        },
        missingInterpolationHandler(text, value) {
            const message = `Missing translation key [${text}] for ${value}`
            console.warn(message)
        },
    })

export default i18n
